import '../assets/NcDateTimePickerNative-DLch-JRA.css';
import { S as ScopeComponent } from "./ScopeComponent-59SnWk9S.mjs";
import { openBlock, createElementBlock, createElementVNode, normalizeClass, toDisplayString, mergeProps } from "vue";
import { _ as _export_sfc } from "./_plugin-vue_export-helper-1tPrXgE0.mjs";
const inputDateTypes = ["date", "datetime-local", "month", "time", "week"];
const _sfc_main = ScopeComponent({
  name: "NcDateTimePickerNative",
  inheritAttrs: false,
  props: {
    /**
     * The date is – like the `Date` object in JavaScript – tied to UTC.
     * The selected time zone does not have an influence of the selected time and date value.
     * You have to translate the time yourself when you want to factor in time zones.
     * Pass null to clear the input field.
     */
    modelValue: {
      type: Date,
      default: null
    },
    /**
     * id attribute of the input field
     */
    id: {
      type: String,
      required: true
    },
    /**
     * type attribute of the input field
     * default type: String
     * The type of the input element, it can be `date`, `datetime-local`, `month`, `time`, `week`
     */
    type: {
      type: String,
      default: "date",
      validate: (name) => inputDateTypes.includes(name)
    },
    /**
     * text inside the label element
     * default type: String
     */
    label: {
      type: String,
      default: "Please choose a date"
    },
    /**
     * min attribute of the input field
     * default type: null
     */
    min: {
      type: [Date, Boolean],
      default: null
    },
    /**
     * max attribute of the input field
     * default type: null
     */
    max: {
      type: [Date, Boolean],
      default: null
    },
    /**
     * Flag to hide the label
     * default type: String
     * The hidden input label for accessibility purposes.
     */
    hideLabel: {
      type: Boolean,
      default: false
    },
    /**
     * Class to add to the input field.
     * Necessary to use NcDateTimePickerNative in the NcActionInput component.
     */
    inputClass: {
      type: [Object, String],
      default: ""
    }
  },
  emits: [
    "update:modelValue"
  ],
  computed: {
    formattedValue() {
      return this.formatValue(this.modelValue);
    },
    formattedMin() {
      if (this.min) {
        return this.formatValue(this.min);
      }
      return false;
    },
    formattedMax() {
      if (this.max) {
        return this.formatValue(this.max);
      }
      return false;
    }
  },
  methods: {
    /**
     * Handle the input event
     *
     * @param {InputEvent} $event input event payload
     * @return {Date|null} new chosen Date() or null
     */
    onInput($event) {
      if (isNaN($event.target.valueAsNumber)) {
        return this.$emit("update:modelValue", null);
      }
      if (this.type === "time") {
        const time = $event.target.value;
        if (this.modelValue === "") {
          const { yyyy: yyyy2, MM: MM2, dd: dd2 } = this.getReadableDate(/* @__PURE__ */ new Date());
          return this.$emit("update:modelValue", /* @__PURE__ */ new Date(`${yyyy2}-${MM2}-${dd2}T${time}`));
        }
        const { yyyy, MM, dd } = this.getReadableDate(this.modelValue);
        return this.$emit("update:modelValue", /* @__PURE__ */ new Date(`${yyyy}-${MM}-${dd}T${time}`));
      } else if (this.type === "month") {
        const MM = (new Date($event.target.value).getMonth() + 1).toString().padStart(2, "0");
        if (this.modelValue === "") {
          const { yyyy: yyyy2, dd: dd2, hh: hh2, mm: mm2 } = this.getReadableDate(/* @__PURE__ */ new Date());
          return this.$emit("update:modelValue", /* @__PURE__ */ new Date(`${yyyy2}-${MM}-${dd2}T${hh2}:${mm2}`));
        }
        const { yyyy, dd, hh, mm } = this.getReadableDate(this.value);
        return this.$emit("update:modelValue", /* @__PURE__ */ new Date(`${yyyy}-${MM}-${dd}T${hh}:${mm}`));
      }
      const timezoneOffsetSeconds = new Date($event.target.valueAsNumber).getTimezoneOffset() * 1e3 * 60;
      const inputDateWithTimezone = $event.target.valueAsNumber + timezoneOffsetSeconds;
      return this.$emit("update:modelValue", new Date(inputDateWithTimezone));
    },
    /**
     * Returns Object with string values of a Date
     *
     * @param {Date} value The selected value
     * @return {object|undefined}
     */
    getReadableDate(value) {
      if (value instanceof Date) {
        const yyyy = value.getFullYear().toString().padStart(4, "0");
        const MM = (value.getMonth() + 1).toString().padStart(2, "0");
        const dd = value.getDate().toString().padStart(2, "0");
        const hh = value.getHours().toString().padStart(2, "0");
        const mm = value.getMinutes().toString().padStart(2, "0");
        return { yyyy, MM, dd, hh, mm };
      }
    },
    /**
     * Returns preformatted value for the input field
     *
     * @param {Date} value The selected value
     * @return {string|undefined}
     */
    formatValue(value) {
      if (value instanceof Date) {
        const { yyyy, MM, dd, hh, mm } = this.getReadableDate(value);
        if (this.type === "datetime-local") {
          return `${yyyy}-${MM}-${dd}T${hh}:${mm}`;
        } else if (this.type === "date") {
          return `${yyyy}-${MM}-${dd}`;
        } else if (this.type === "month") {
          return `${yyyy}-${MM}`;
        } else if (this.type === "time") {
          return `${hh}:${mm}`;
        } else if (this.type === "week") {
          const startDate = new Date(yyyy, 0, 1);
          const daysSinceBeginningOfYear = Math.floor((value - startDate) / (24 * 60 * 60 * 1e3));
          const weekNumber = Math.ceil(daysSinceBeginningOfYear / 7);
          return `${yyyy}-W${weekNumber}`;
        }
      } else {
        return "";
      }
    }
  }
});
const _hoisted_1 = { class: "native-datetime-picker" };
const _hoisted_2 = ["for"];
const _hoisted_3 = ["id", "type", "value", "min", "max"];
function _sfc_render(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", _hoisted_1, [
    createElementVNode("label", {
      class: normalizeClass({ "hidden-visually": _ctx.hideLabel }),
      for: _ctx.id
    }, toDisplayString(_ctx.label), 11, _hoisted_2),
    createElementVNode("input", mergeProps({
      id: _ctx.id,
      class: ["native-datetime-picker--input", _ctx.inputClass],
      type: _ctx.type,
      value: _ctx.formattedValue,
      min: _ctx.formattedMin,
      max: _ctx.formattedMax
    }, _ctx.$attrs, {
      onInput: _cache[0] || (_cache[0] = (...args) => _ctx.onInput && _ctx.onInput(...args))
    }), null, 16, _hoisted_3)
  ]);
}
const NcDateTimePickerNative = /* @__PURE__ */ _export_sfc(_sfc_main, [["render", _sfc_render], ["__scopeId", "data-v-2debd3a9"]]);
export {
  NcDateTimePickerNative as N
};
